import { useState } from 'react';

import loadable from '@loadable/component';

import { Button } from '@simplywallst/ui-core/next';

import WarningSolid from '@simplywallst/ui-core/icons/WarningSolid';

import { pluralise } from '@/utilities/pluralise';

import { CompanyIcon } from '@/components/CompanyIcon';

import type { InvalidHolding, Portfolio } from '@/hooks/portfolio/usePortfolio';

const InvalidHoldingsModal = loadable(() => import('../InvalidHoldingsModal'), {
  resolveComponent: (c) => c.InvalidHoldingsModal,
});

type Props = {
  portfolioId: Portfolio['id'];
  invalidHoldings: InvalidHolding[];
  initialModalState?: boolean;
};

export const InvalidHoldingsBanner = ({
  portfolioId,
  invalidHoldings,
  initialModalState = false,
}: Props) => {
  const [state, setState] = useState(initialModalState);
  return (
    <div className="mx-x2 mt-x2 rounded-x1 bg-legacy-phantom p-x1_5 pb-x2 shadow-soft md:mb-x2 lg:mx-x0 lg:mt-x1">
      <div className="grid grid-cols-[24px_1fr_max-content] items-start gap-x-x1">
        <svg width="24" height="24" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="6" fill="#FFF" />
          <WarningSolid className="fill-warning" />
        </svg>
        <p className="m-0 mb-x1 mt-x0_25 text-xs">
          {`${invalidHoldings.length} invalid ${pluralise(
            invalidHoldings.length,
            'holding',
            'holdings'
          )}. ${pluralise(
            invalidHoldings.length,
            `It has`,
            `They've`
          )} been excluded from your portfolio until fixed.`}
        </p>
        <Button variant="outline" size="medium" onClick={() => setState(true)}>
          See errors
        </Button>
        {state && (
          <InvalidHoldingsModal
            portfolioId={portfolioId}
            invalidHoldings={invalidHoldings}
            onClose={() => setState(false)}
          />
        )}
        <div className="col-[2_/_span_2] grid grid-cols-[max-content_max-content] items-center">
          <div className="flex">
            {invalidHoldings.slice(0, 6).map((n) => (
              <div key={`invalid-logo-${n.id}`} className="mr-x1 flex-none">
                <CompanyIcon
                  tickerSymbol={n.symbol}
                  website={n.company.info?.url}
                  size={32}
                />
              </div>
            ))}
            {invalidHoldings.length > 6 && (
              <div className="mr-x1 grid size-x4 items-center rounded-x0_75 bg-transparent-strong text-center text-xs text-soft">
                <span>{`+${invalidHoldings.length - 6}`}</span>
              </div>
            )}
          </div>
          <div className="hidden text-xs text-soft md:block">
            {invalidHoldings
              .slice(0, 6)
              .map((n) => n.symbol)
              .join(', ')}
            {invalidHoldings.length > 6 &&
              ` +${invalidHoldings.length - 6} more`}
          </div>
        </div>
      </div>
    </div>
  );
};
